// Notification Service for vgoride
// Handles both browser push notifications and in-app notifications

class NotificationService {
  constructor() {
    this.permission = 'default';
    this.swRegistration = null;
    this.subscribers = [];
  }

  // Initialize the notification service
  async init() {
    // Check if notifications are supported
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return false;
    }

    // Check if service workers are supported
    if (!('serviceWorker' in navigator)) {
      console.warn('Service workers are not supported');
      return false;
    }

    // Register service worker
    try {
      this.swRegistration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', this.swRegistration);
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return false;
    }

    this.permission = Notification.permission;
    return true;
  }

  // Request notification permission
  async requestPermission() {
    if (!('Notification' in window)) {
      return 'denied';
    }

    if (Notification.permission === 'granted') {
      this.permission = 'granted';
      return 'granted';
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      return permission;
    }

    return Notification.permission;
  }

  // Check if notifications are enabled
  isEnabled() {
    return this.permission === 'granted';
  }

  // Show a browser notification
  async showNotification(title, options = {}) {
    if (!this.isEnabled()) {
      console.warn('Notifications not enabled');
      return null;
    }

    const defaultOptions = {
      icon: '/logo.png',
      badge: '/logo.png',
      vibrate: [200, 100, 200],
      tag: 'vgoride-notification',
      requireInteraction: false,
      ...options
    };

    // Use service worker to show notification if available
    if (this.swRegistration) {
      return this.swRegistration.showNotification(title, defaultOptions);
    }

    // Fallback to regular notification
    return new Notification(title, defaultOptions);
  }

  // Show ride reminder notification
  async showRideReminder(ride, minutesBefore) {
    const title = `Upcoming Ride in ${minutesBefore} minutes`;
    const body = `Pickup: ${ride.pickup_address}`;
    
    return this.showNotification(title, {
      body,
      tag: `ride-reminder-${ride.id}`,
      data: {
        rideId: ride.id,
        type: 'ride_reminder',
        url: '/driver'
      },
      actions: [
        { action: 'navigate', title: 'Navigate' },
        { action: 'dismiss', title: 'Dismiss' }
      ],
      requireInteraction: true
    });
  }

  // Subscribe to notification updates
  subscribe(callback) {
    this.subscribers.push(callback);
    return () => {
      this.subscribers = this.subscribers.filter(cb => cb !== callback);
    };
  }

  // Notify all subscribers
  notifySubscribers(notification) {
    this.subscribers.forEach(cb => cb(notification));
  }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService;
